import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { useGetOneDocumentQuery, useLazyGetDocumentProgrammationQuery, usePutOneDocumentProgrammationMutation } from "../../../../services/api/repositories/DocumentsRepository";
import { useNavigate, useParams } from "react-router-dom";
import DatePicker from 'react-datepicker';
import PutDocumentProgrammationQuery from "../../../../models/api/Documents/PutDocumentProgrammationQuery";

export default function DocumentEditSection() {

    const navigate = useNavigate();
    const { uuid } = useParams<{ uuid: string }>();
    const [showValidation, setShowValidation] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{ dateStart: string, dateEnd: string }>({ dateStart: "", dateEnd: "" });

    const { data: documentData, isLoading: isLoadingDocument } = useGetOneDocumentQuery({ uuid: uuid || '' });
    
    const [getDocumentProgrammation, { data: programmationData }] = useLazyGetDocumentProgrammationQuery();
    const [putDocumentProgrammation, { isSuccess, isLoading, isError, error }] = usePutOneDocumentProgrammationMutation();

    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    
    const [isCanceled, setIsCanceled] = useState(false);

    const [show, setShow] = useState(false);
    const [errMsg, setErrMsg] = useState<string>("");

    useEffect(() => {
        if (uuid) {
            getDocumentProgrammation({ uuid });
        }
    }, [uuid, getDocumentProgrammation]);

    useEffect(() => {
        if (programmationData && !('message' in programmationData)) {
            setStartDate(programmationData.date_validity_start ? new Date(programmationData.date_validity_start) : null);
            setEndDate(programmationData.date_validity_end ? new Date(programmationData.date_validity_end) : null);
            setIsCanceled(programmationData.is_canceled || false);
        }
    }, [programmationData]);

    const handleClose = useCallback(() => {
        setShow(false);
    }, []);

    const handleShow = useCallback(() => {
        setShowValidation(true);

        let errors = validationErrors;
        let canSend = true;

        const hasNoStartDate = !startDate;
        const hasInvalidDateRange = startDate && endDate && startDate > endDate;

        if (hasNoStartDate) {
            errors.dateStart = "La date de début est obligatoire";
            canSend = false;
        }

        if (hasInvalidDateRange) {
            errors.dateEnd = "La date de fin doit être postérieure à la date de début";
            canSend = false;
        }

        if (!canSend) {
            setValidationErrors(errors);
            return;
        }

        setShow(true);
    }, [startDate, endDate, validationErrors]);

    const handleError = useCallback(() => {
        if (isError && error !== undefined && error !== null && typeof error === 'object') {
            if ("error" in error) {
                setErrMsg(error.error as string);
            } else if ("data" in error && typeof error.data === 'object' && error.data !== null) {
                if ("message" in error.data) {
                    setErrMsg(error.data.message as string);
                } else if ("errors" in error.data) {
                    const errors = error.data.errors as Record<string, string[]>;
                    const errorMessages = Object.values(errors).flat();
                    setErrMsg(errorMessages.join(', '));
                }
            }
        } else if (errMsg === "") {
            setErrMsg("Une erreur a été rencontrée");
        }
    }, [isError, error, errMsg]);

    useEffect(() => {
        if (isSuccess) {
            navigate("/documents");
        }

        if (isError) {
            handleError();
        }
    }, [isSuccess, navigate, isError, handleError]);

    const handleSubmit = useCallback(async () => {
        if (!uuid) return;

        const formatDate = (date: Date) => {
            return `${date.toLocaleDateString()} 00:00:00`;
        };

        const date_validity_start = startDate ? formatDate(startDate) : undefined;
        const date_validity_end = endDate ? formatDate(endDate) : undefined;

        putDocumentProgrammation({
            uuid,
            data: new PutDocumentProgrammationQuery({
                is_canceled: isCanceled,
                date_validity_start,
                date_validity_end
            })
        });

        setShow(false);
    }, [uuid, startDate, endDate, isCanceled, putDocumentProgrammation]);

    if (isLoadingDocument || !documentData || 'message' in documentData) {
        return <div>Chargement...</div>;
    }

    if (documentData.type !== 'legal_cgv') {
        return <div>Ce document ne peut pas être programmé</div>;
    }

    return (
        <>
            <div>
                <div className="object attach">
                    <Form className="form" validated={showValidation}>
                        <section className="form-section id">
                            <h3>
                                <span className="icon"></span>
                                <span className="text">Définition</span>
                            </h3>
                            <Form.Group className="form-group attach-description">
                                <Form.Label className="form-label">Description</Form.Label>
                                <Form.Control
                                    type="text"
                                    className="form-control w100"
                                    value={documentData.description}
                                    disabled
                                />
                            </Form.Group>
                            <Form.Group className="form-group attach-type">
                                <Form.Label className="form-label">Type</Form.Label>
                                <Form.Control
                                    type="text"
                                    className="form-control w100"
                                    value={documentData.type}
                                    disabled
                                />
                            </Form.Group>
                            <Form.Group className="form-group client-types">
                                <Form.Label className="form-label">Clients cibles</Form.Label>
                                <Form.Control
                                    type="text"
                                    className="form-control w100"
                                    value={documentData.client_types.map(clientType => clientType.name).join(", ")}
                                    disabled
                                />
                            </Form.Group>
                        </section>

                        <section className="form-section schedule">
                            <h3><span className="icon"></span><span className="text">Programmation</span></h3>
                            <div className="fromTo">
                                <Form.Group className="form-group mailing-runDateStart">
                                    <Form.Label className="form-label" htmlFor="mailing-runDateStart-input">Du</Form.Label>
                                    <DatePicker 
                                        className={`form-control w100 ${validationErrors.dateStart.length > 0 ? 'is-invalid' : ''}`}
                                        id="mailing-runDateStart-input" 
                                        dateFormat="dd/MM/yyyy"
                                        selected={startDate}
                                        onChange={(date) => setStartDate(date)}
                                    />
                                    <Form.Control.Feedback type="invalid">{validationErrors.dateStart}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group className="form-group mailing-runDateEnd">
                                    <Form.Label className="form-label" htmlFor="mailing-runDateEnd-input">Au</Form.Label>
                                    <DatePicker
                                        className={`form-control w100 ${validationErrors.dateEnd.length > 0 ? 'is-invalid' : ''}`}
                                        id="mailing-runDateEnd-input"
                                        dateFormat="dd/MM/yyyy"
                                        selected={endDate}
                                        onChange={(date) => setEndDate(date)}
                                    />
                                    <Form.Control.Feedback type="invalid">{validationErrors.dateEnd}</Form.Control.Feedback>
                                </Form.Group>
                            </div>
                            <Form.Group className="form-group mt-3">
                                <Form.Check 
                                    type="switch"
                                    id="is-canceled-switch"
                                    label="Annuler la programmation"
                                    checked={isCanceled}
                                    onChange={(e) => setIsCanceled(e.target.checked)}
                                />
                            </Form.Group>
                        </section>

                        <div>
                            {Object.values(validationErrors).some(error => error.length > 0) && (
                                <Alert variant="danger" dismissible>
                                    {Object.values(validationErrors)
                                        .filter(error => error.length > 0)
                                        .map((error, index) => (
                                            <div key={index}>{error}</div>
                                        ))}
                                </Alert>
                            )}

                            {isError && errMsg && (
                                <Alert variant="danger" dismissible>
                                    {errMsg}
                                </Alert>
                            )}
                        </div>

                        <div className="form-submit">
                            <Button type="button" className="btn btn-secondary btn-cancel" onClick={() => navigate("/documents")}>
                                <span className="icon"></span>
                                <span className="text">Annuler</span>
                            </Button>
                            <Button type="button" className="btn btn-primary btn-ok" onClick={handleShow}>
                                <span className="icon"></span>
                                <span className="text">Modifier</span>
                            </Button>
                        </div>
                    </Form>
                </div>
            </div>
            <SubmitModal 
                show={show} 
                handleClose={handleClose} 
                handleSubmit={handleSubmit} 
                name={documentData.name}
                startDate={startDate?.toLocaleDateString()}
                endDate={endDate?.toLocaleDateString()}
            />
        </>
    );
}

function SubmitModal({ show, handleClose, handleSubmit, name, startDate, endDate }: { show: boolean, handleClose: () => void, handleSubmit: () => void, name: string, startDate?: string, endDate?: string }) {
    return (
        <Modal id="documentEditConfirmationModal" show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <h1 className="modal-title" id="documentEditConfirmationModalLabel">
                    <span className="icon"></span>
                    <span className="text">Modification d'une pièce jointe</span>
                </h1>
            </Modal.Header>
            <Modal.Body>
                <p>Vous souhaitez modifier la programmation de la pièce jointe&nbsp;:</p>
                <div className="object attach" data-status="active">
                    <h2>
                        <span className="icon"></span>
                        <span className="text">{name}</span>
                    </h2>
                    {startDate && (
                        <p>En exploitation à partir du {startDate} {endDate && `jusqu'au ${endDate}`}</p>
                    )}
                </div>
                <p>Veuillez valider la modification de cette programmation.</p>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="secondary"
                    className="btn-cancel"
                    data-bs-dismiss="modal"
                    title="Annuler"
                    onClick={handleClose}
                >
                    <span className="icon"></span>
                    Annuler
                </Button>
                <Button
                    variant="primary"
                    className="btn-ok"
                    data-bs-dismiss="modal"
                    title="Valider"
                    onClick={handleSubmit}
                >
                    <span className="icon"></span>
                    Valider
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
